import { mean } from './aggregations';
import { euclideanDistance } from './similarity';

const vectorNorm = (vector) => Math.sqrt(vector.reduce((sum, value) => sum + value * value, 0));

export const kMeans = (profiles, k = 3, maxIterations = 50) => {
  const points = (profiles || []).filter((profile) => Array.isArray(profile.vector) && profile.vector.length > 0);
  if (points.length === 0) {
    return { clusters: new Map(), centroids: [], members: [], iterations: 0, inertia: 0 };
  }

  const clusterCount = Math.max(1, Math.min(k, points.length));
  const sorted = [...points].sort((a, b) => vectorNorm(b.vector) - vectorNorm(a.vector));
  const centroids = Array.from({ length: clusterCount }, (_, index) => {
    const position = clusterCount === 1
      ? 0
      : Math.round((index * (sorted.length - 1)) / (clusterCount - 1));
    return [...sorted[position].vector];
  });

  let assignments = new Map();
  let iterations = 0;

  for (let iteration = 0; iteration < maxIterations; iteration += 1) {
    iterations = iteration + 1;
    const nextAssignments = new Map();
    const groups = Array.from({ length: clusterCount }, () => []);
    let changed = false;
    const previousAssignments = assignments;

    points.forEach((point) => {
      let bestIndex = 0;
      let bestDistance = Infinity;
      centroids.forEach((centroid, index) => {
        const distance = euclideanDistance(point.vector, centroid);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      });
      nextAssignments.set(point.id, bestIndex);
      groups[bestIndex].push(point);
      if (previousAssignments.get(point.id) !== bestIndex) changed = true;
    });

    groups.forEach((group, index) => {
      if (group.length === 0) return;
      centroids[index] = group[0].vector.map((_, dimension) =>
        mean(group.map((point) => point.vector[dimension] || 0)),
      );
    });

    assignments = nextAssignments;
    if (!changed) break;
  }

  const members = Array.from({ length: clusterCount }, () => []);
  assignments.forEach((clusterIndex, id) => members[clusterIndex].push(id));

  const inertia = points.reduce((sum, point) => {
    const centroid = centroids[assignments.get(point.id)] || [];
    return sum + euclideanDistance(point.vector, centroid) ** 2;
  }, 0);

  return { clusters: assignments, centroids, members, iterations, inertia };
};

export const nearestCluster = (vector, centroids) => {
  let bestIndex = 0;
  let bestDistance = Infinity;
  (centroids || []).forEach((centroid, index) => {
    const distance = euclideanDistance(vector, centroid);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = index;
    }
  });
  return bestIndex;
};
